import VSignature from '@voly/ui-components/src/v2/VSignature/VSignature';
import { ReactElement, useState } from 'react';
import VEmptyState from '@voly/ui-components/src/v2/VEmptyState';
import VMaterialIcon from '@voly/ui-components/src/v2/icons/VMaterialIcon';
import VButton from '@voly/ui-components/src/v2/VButton';
import VStackRow from '@voly/ui-components/src/v2/VStackRow';
import VStack from '@voly/ui-components/src/v2/VStack';
import VBox from '@voly/ui-components/src/v2/VBox';
import VText from '@voly/ui-components/src/v2/VText';
import { translate } from 'src/app/project/features/translate/translate';
import RemoveDigitalSignatureModal from '../molecules/RemoveDigitalSignatureModal';
import AddDigitalSignatureModal, {
  type TDigitalSignatureValues,
} from '../molecules/AddDigitalSignatureModal';

export interface DigitalSignatureProps {
  onSave: (values: TDigitalSignatureValues) => void;
  onRemove: () => void;
  signature: {
    user: string;
    role: string;
    svg: Blob | null;
  };
}

const DigitalSignature = ({ onSave, onRemove, signature }: DigitalSignatureProps): ReactElement => {
  const [isRemoveDigitalSignatureModalOpen, setIsRemoveDigitalSignatureModalOpen] = useState(false);
  const [isAddDigitalSignatureModalOpen, setIsAddDigitalSignatureModalOpen] = useState(false);

  const handleAddSignature = () => {
    setIsAddDigitalSignatureModalOpen(true);
  };

  const handleOpenRemoveSignatureModal = () => {
    setIsRemoveDigitalSignatureModalOpen(true);
  };

  const handleRemoveSignature = () => {
    setIsRemoveDigitalSignatureModalOpen(false);
    onRemove();
  };

  const handleSaveNewSignature = (values: TDigitalSignatureValues) => {
    setIsAddDigitalSignatureModalOpen(false);
    onSave(values);
  };

  return (
    <>
      <AddDigitalSignatureModal
        open={isAddDigitalSignatureModalOpen}
        onClose={() => setIsAddDigitalSignatureModalOpen(false)}
        onSave={(values) => handleSaveNewSignature(values)}
        user={{ name: signature.user, role: signature.role, svg: null }}
      />

      <RemoveDigitalSignatureModal
        open={isRemoveDigitalSignatureModalOpen}
        onClose={() => setIsRemoveDigitalSignatureModalOpen(false)}
        onDelete={handleRemoveSignature}
      />

      <VBox borderWidth="1px" borderRadius="md" backgroundColor={'white'}>
        <VBox>
          <VText borderBottomWidth="1px" p={4} textStyle="xl" fontWeight="bold" mb={4}>
            {translate('digital_signature')}
          </VText>
        </VBox>

        {signature.svg ? (
          <VBox
            borderRadius="sm"
            backgroundColor={'white'}
            m={4}
            gap={4}
            flexDirection={'column'}
            display={'flex'}
          >
            <VSignature readOnly signature={signature.svg} />

            <VStackRow justifyContent={'space-between'} alignItems={'center'}>
              <VStack gap={0} alignItems={'start'}>
                <VText textSize="sm">Signature</VText>
                <VText fontWeight="bold">
                  {signature.user} {signature.role && `- ${signature.role}`}
                </VText>
              </VStack>
              <VButton color="secondary" onClick={handleOpenRemoveSignatureModal}>
                <VMaterialIcon name="delete" />
                Remove
              </VButton>
            </VStackRow>
          </VBox>
        ) : (
          <VBox borderWidth="1px" borderRadius="sm" backgroundColor={'white'} m={4}>
            <VEmptyState
              icon={<VMaterialIcon name="edit" />}
              title={translate('no_signature_saved')}
              description={translate('no_signature_saved_message')}
            >
              <VButton onClick={handleAddSignature}>{translate('add_signature')}</VButton>
            </VEmptyState>
          </VBox>
        )}
      </VBox>
    </>
  );
};

export default DigitalSignature;
